import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/layout/Header";
import { useAuth } from "@/lib/AuthProvider";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PawPrint, CalendarDays, Plus, LogOut, User, Trash2 } from "lucide-react";
export default function Portal() {
  const { user, loading, login, logout } = useAuth();
  const nav = useNavigate();
  const [pets, setPets] = useState([]);
  const [appts, setAppts] = useState([]);
  const [tab, setTab] = useState("overview");
  const [showAddPet, setShowAddPet] = useState(false);
  const [newPet, setNewPet] = useState({ name: "", species: "Dog", breed: "", dob: "", weight: "", color: "", allergies: "", conditions: "", notes: "" });

  useEffect(() => {
    if (!loading && !user) return;
    if (user) {
      api.get("/pets").then((r) => setPets(r.data));
      api.get("/appointments").then((r) => setAppts(r.data));
    }
  }, [user, loading]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><p className="text-[#4b5563] text-sm">Loading…</p></div>;

  if (!user) {
    return (
      <div className="min-h-screen ankara flex items-center justify-center p-4" data-testid="portal-login">
        <div className="max-w-md w-full text-center bg-white border border-[#EAD9B0] rounded-3xl p-10">
          <img src="/logo.png" alt="Jay's" className="h-24 mx-auto" />
          <h1 className="font-display text-2xl mt-4">Sign in to your portal</h1>
          <p className="mt-2 text-sm text-[#57534E]">Manage pets, appointments and records.</p>
          <button onClick={login} className="mt-6 btn-primary w-full py-3 rounded-full text-sm font-semibold" data-testid="portal-signin-btn">
            Continue with Google
          </button>
          <Link to="/" className="mt-4 inline-block text-xs text-[#57534E]">← Back to site</Link>
        </div>
      </div>
    );
  }

  const addPet = async () => {
    try {
      const payload = { ...newPet, weight: newPet.weight ? parseFloat(newPet.weight) : null };
      const { data } = await api.post("/pets", payload);
      setPets([...pets, data]);
      setShowAddPet(false);
      setNewPet({ name: "", species: "Dog", breed: "", dob: "", weight: "", color: "", allergies: "", conditions: "", notes: "" });
      toast.success(`${data.name} added`);
    } catch { toast.error("Could not add pet"); }
  };

  const delPet = async (id) => {
    if (!confirm("Remove this pet?")) return;
    await api.delete(`/pets/${id}`);
    setPets(pets.filter((p) => p.pet_id !== id));
    toast.success("Pet removed");
  };

  const cancelAppt = async (id) => {
    if (!confirm("Cancel this appointment?")) return;
    await api.post(`/appointments/${id}/cancel`);
    setAppts(appts.map((a) => a.appointment_id === id ? { ...a, status: "cancelled" } : a));
    toast.success("Appointment cancelled");
  };

  const upcoming = appts.filter((a) => a.status === "confirmed" && new Date(a.start_at) > new Date());
  const past = appts.filter((a) => a.status !== "confirmed" || new Date(a.start_at) <= new Date());

  return (
    <div className="min-h-screen ankara" data-testid="portal">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <div className="chip chip-emerald mb-3">Client portal</div>
            <h1 className="font-display text-3xl md:text-4xl mt-1">Welcome, {user.name.split(" ")[0]} 🐾</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/book" className="btn-primary px-5 py-2.5 rounded-full text-sm font-semibold" data-testid="portal-book-btn">Book appointment</Link>
          </div>
        </div>

        <div className="flex gap-2 border-b border-[#EAD9B0] mb-6 overflow-x-auto" data-testid="portal-tabs">
          {["overview", "pets", "appointments"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
              className={`px-4 py-2 text-sm font-medium capitalize border-b-2 -mb-px whitespace-nowrap ${tab === t ? "border-[#2B5B84] text-[#2B5B84]" : "border-transparent text-[#4b5563] hover:text-[#1F2937]"}`}>
              {t}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card icon={PawPrint} label="My pets" value={pets.length} testId="stat-pets" />
            <Card icon={CalendarDays} label="Upcoming" value={upcoming.length} testId="stat-upcoming" />
            <Card icon={User} label="Member since" value={new Date(user.created_at).getFullYear()} testId="stat-member" />
            <div className="lg:col-span-3 bg-white border border-[#EAD9B0] rounded-2xl p-6">
              <h3 className="font-display text-lg mb-4">Next appointment</h3>
              {upcoming[0] ? (
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{upcoming[0].service_name} · {upcoming[0].pet_name}</div>
                    <div className="text-sm text-[#4b5563]">{new Date(upcoming[0].start_at).toLocaleString([], { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>
                  </div>
                  <span className="text-xs text-[#8A9A5B]">Confirmed</span>
                </div>
              ) : <p className="text-sm text-[#4b5563]">No upcoming visits. <Link to="/book" className="text-[#2B5B84] underline">Book one</Link>.</p>}
            </div>
          </div>
        )}

        {tab === "pets" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-display text-xl">Your pets</h2>
              <button onClick={() => setShowAddPet(!showAddPet)} className="btn-gold px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1" data-testid="add-pet-toggle">
                <Plus size={14} /> Add pet
              </button>
            </div>
            {showAddPet && (
              <div className="mb-6 p-5 bg-white border border-[#EAD9B0] rounded-2xl grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="add-pet-form">
                {[
                  ["name", "Name"],
                  ["breed", "Breed"],
                  ["dob", "Date of birth (YYYY-MM-DD)"],
                  ["weight", "Weight (kg)"],
                  ["color", "Color"],
                  ["allergies", "Allergies"],
                ].map(([k, label]) => (
                  <input key={k} placeholder={label} value={newPet[k]} onChange={(e) => setNewPet({ ...newPet, [k]: e.target.value })}
                    data-testid={`petfield-${k}`} className="rounded-lg border border-[#EAD9B0] bg-[#FFF7E6] p-2.5 text-sm" />
                ))}
                <select value={newPet.species} onChange={(e) => setNewPet({ ...newPet, species: e.target.value })} data-testid="petfield-species"
                  className="rounded-lg border border-[#EAD9B0] bg-[#FFF7E6] p-2.5 text-sm">
                  {["Dog", "Cat", "Rabbit", "Bird", "Reptile", "Other"].map((s) => <option key={s}>{s}</option>)}
                </select>
                <textarea placeholder="Existing conditions" value={newPet.conditions} onChange={(e) => setNewPet({ ...newPet, conditions: e.target.value })}
                  data-testid="petfield-conditions" className="md:col-span-2 rounded-lg border border-[#EAD9B0] bg-[#FFF7E6] p-2.5 text-sm" rows={2} />
                <button onClick={addPet} disabled={!newPet.name} className="md:col-span-3 btn-primary py-2.5 rounded-full text-sm font-semibold disabled:opacity-50" data-testid="save-pet-btn">Save pet</button>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pets.map((p) => (
                <div key={p.pet_id} className="bg-white border border-[#EAD9B0] rounded-2xl p-5" data-testid={`pet-card-${p.pet_id}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-display text-lg">{p.name}</div>
                      <div className="text-xs text-[#4b5563] mt-1">{p.species}{p.breed ? ` · ${p.breed}` : ""}</div>
                    </div>
                    <button onClick={() => delPet(p.pet_id)} className="text-[#4b5563] hover:text-red-600" data-testid={`del-pet-${p.pet_id}`}><Trash2 size={14} /></button>
                  </div>
                  {p.dob && <div className="mt-3 text-xs text-[#4b5563]">Born {p.dob}</div>}
                  {p.weight && <div className="text-xs text-[#4b5563]">{p.weight} kg</div>}
                  {p.allergies && <div className="mt-2 text-xs"><b>Allergies:</b> {p.allergies}</div>}
                </div>
              ))}
              {pets.length === 0 && <p className="text-sm text-[#4b5563]">No pets yet. Add one above to get started.</p>}
            </div>
          </div>
        )}

        {tab === "appointments" && (
          <div className="space-y-8">
            <div>
              <h2 className="font-display text-xl mb-4">Upcoming</h2>
              <div className="space-y-3">
                {upcoming.map((a) => (
                  <ApptRow key={a.appointment_id} a={a} onCancel={() => cancelAppt(a.appointment_id)} />
                ))}
                {upcoming.length === 0 && <p className="text-sm text-[#4b5563]">No upcoming appointments.</p>}
              </div>
            </div>
            <div>
              <h2 className="font-display text-xl mb-4">Past</h2>
              <div className="space-y-3">
                {past.slice(0, 20).map((a) => <ApptRow key={a.appointment_id} a={a} />)}
                {past.length === 0 && <p className="text-sm text-[#4b5563]">No past visits.</p>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Card({ icon: Icon, label, value, testId }) {
  return (
    <div className="bg-white border border-[#EAD9B0] rounded-2xl p-6" data-testid={testId}>
      <Icon size={20} className="text-[#2B5B84]" />
      <div className="mt-3 text-xs uppercase tracking-widest text-[#4b5563]">{label}</div>
      <div className="font-display text-3xl mt-1">{value}</div>
    </div>
  );
}

function ApptRow({ a, onCancel }) {
  const status = { confirmed: "#8A9A5B", cancelled: "#dc2626", completed: "#2B5B84", waitlist: "#D4AF37" }[a.status];
  return (
    <div className="bg-white border border-[#EAD9B0] rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6" data-testid={`appt-${a.appointment_id}`}>
      <div className="flex-1">
        <div className="font-medium">{a.service_name} · {a.pet_name}</div>
        <div className="text-xs text-[#4b5563]">{new Date(a.start_at).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} · {a.vet_name || "Any vet"} · {a.mode}</div>
        {a.mode === "virtual" && a.meeting_link && a.status === "confirmed" && (
          <a href={a.meeting_link} target="_blank" rel="noopener noreferrer" data-testid={`join-video-${a.appointment_id}`}
            className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#0F766E] hover:underline">
            📹 Join Video Call ({a.meeting_provider || "GoWorldMet"})
          </a>
        )}
        {a.mode === "virtual" && !a.meeting_link && a.status === "confirmed" && (
          <div className="mt-1 text-[11px] text-[#57534E]">📹 Video link will be sent by Dr. Juwon before your appointment.</div>
        )}
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs uppercase tracking-widest flex items-center" style={{ color: status }}>
          <span className="status-dot" style={{ background: status }} />{a.status}
        </span>
        {onCancel && a.status === "confirmed" && <button onClick={onCancel} className="text-xs text-red-600 hover:underline" data-testid={`cancel-${a.appointment_id}`}>Cancel</button>}
      </div>
    </div>
  );
}
